import { X, Loader2, AlertTriangle, RefreshCw } from "lucide-react";

export function AdminHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-display">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}

export function StatCard({ label, value, icon: Icon, tone = "slate" }) {
  const tones = { slate: "text-slate-600 bg-slate-100", amber: "text-amber-600 bg-amber-100", emerald: "text-emerald-600 bg-emerald-100", red: "text-red-600 bg-red-100" };
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        {Icon && <span className={`h-9 w-9 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" /></span>}
      </div>
      <p className="text-2xl font-bold text-slate-900 font-mono mt-2">{value}</p>
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", loading, ...props }) {
  const styles = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    accent: "bg-amber-500 text-slate-900 hover:bg-amber-400",
    outline: "border border-slate-200 text-slate-700 hover:border-slate-400 bg-white",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };
  return (
    <button {...props} disabled={loading || props.disabled}
      className={`inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-lg transition disabled:opacity-60 ${styles[variant]} ${className}`}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />} {children}
    </button>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Input({ label, testid, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-medium text-slate-600">{label}</label>}
      <input {...props} data-testid={testid}
        className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition" />
    </div>
  );
}

export function Textarea({ label, testid, ...props }) {
  return (
    <div>
      {label && <label className="text-xs font-medium text-slate-600">{label}</label>}
      <textarea {...props} data-testid={testid}
        className="w-full px-3 py-2 mt-1 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
    </div>
  );
}

export function Select({ label, testid, children, ...props }) {
  return (
    <div>
      {label && <label className="text-xs font-medium text-slate-600">{label}</label>}
      <select {...props} data-testid={testid}
        className="w-full h-10 px-3 mt-1 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-amber-400">
        {children}
      </select>
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid, disabled = false }) {
  return (
    <label className={`flex items-center gap-2 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`} data-testid={testid}>
      <button type="button" onClick={() => onChange(!checked)} disabled={disabled}
        aria-pressed={checked} className={`h-6 w-10 rounded-full transition relative ${checked ? "bg-amber-500" : "bg-slate-300"} disabled:cursor-not-allowed`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${checked ? "left-[18px]" : "left-0.5"}`} />
      </button>
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-50 text-emerald-700", inactive: "bg-slate-100 text-slate-500",
    approved: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700",
    rejected: "bg-red-50 text-red-600", hidden: "bg-slate-100 text-slate-500",
    in_stock: "bg-emerald-50 text-emerald-700", low_stock: "bg-amber-50 text-amber-700", out_of_stock: "bg-red-50 text-red-600",
    PAID: "bg-emerald-50 text-emerald-700", PENDING: "bg-amber-50 text-amber-700",
    disabled: "bg-red-50 text-red-600",
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${map[status] || "bg-slate-100 text-slate-600"}`}>{String(status).replace(/_/g, " ")}</span>;
}

export function Empty({ text = "No data yet" }) {
  return <div className="text-center py-12 text-sm text-slate-400" data-testid="admin-empty">{text}</div>;
}

/**
 * Error surface for a failed load/save. `stale` says the data still on screen is the
 * last successful load and NOT freshly updated — the UI must never imply otherwise.
 * An empty state is only ever rendered when the backend really returned no rows.
 */
export function ErrorBanner({ message, onRetry, stale = false, action, testid = "admin-error-banner", className = "" }) {
  return (
    <div role="alert" data-testid={testid}
      className={`rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-3 ${className}`}>
      <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-red-700 break-words">{message}</p>
        {stale && (
          <p className="text-xs text-red-600 mt-1" data-testid="admin-stale-note">
            Showing the last successfully loaded data — it has not been updated.
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {action}
        {onRetry && (
          <Btn variant="outline" onClick={onRetry} data-testid="admin-retry-btn">
            <RefreshCw className="h-4 w-4" /> Retry
          </Btn>
        )}
      </div>
    </div>
  );
}

export function Loading({ text = "Loading…" }) {
  return (
    <div className="py-20 grid place-items-center text-slate-400" data-testid="admin-loading">
      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      <span className="text-xs mt-2">{text}</span>
    </div>
  );
}
